import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
    ShieldCheckIcon,
    SignalIcon,
    EnvelopeIcon,
    LockClosedIcon,
    BugAntIcon,
    ExclamationTriangleIcon,
    LinkIcon,
    EyeIcon,
    PlusIcon,
    ChartBarIcon,
    ChevronDownIcon,
    XMarkIcon,
    ArrowRightIcon,
    GlobeAltIcon,
    FireIcon,
    ClockIcon,
    CheckCircleIcon,
    ServerIcon,
    CpuChipIcon,
    FingerPrintIcon,
    DevicePhoneMobileIcon,
    WrenchScrewdriverIcon,
    BuildingOfficeIcon,
} from "@heroicons/react/20/solid";
import { Subheading } from "src/components/base/heading";
import { Badge } from "src/components/base/badge";
import type { View, SystemView } from "../types";
import { VIEW_LABELS, VIEW_COLORS, SYS_HEX, SYSTEM_LIST } from "../types";
import type { UnifiedEvent } from "../data/events";
import { UNIFIED_EVENTS, THREAT_ACTORS, MITRE_TACTICS, getCorrelationMatrix } from "../data/events";
import { StatCard, Card, SectionTitle, FeedEntry, IncidentChain } from "../shared";
import { WORLD_MAP_PATH, WORLD_MAP_VIEWBOX } from "../worldMapPaths";

/* ═══════════════════════════════════════════════════════════════════════════
   UTILITY: Animated Counter Hook
   ═══════════════════════════════════════════════════════════════════════════ */
function useAnimatedCounter(target: number, duration = 2000): number {
    const [current, setCurrent] = useState(0);
    useEffect(() => {
        const start = performance.now();
        const initial = 0;
        const animate = (now: number) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCurrent(Math.round(initial + (target - initial) * eased));
            if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [target, duration]);
    return current;
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Semi-Circle Gauge
   ═══════════════════════════════════════════════════════════════════════════ */
function SemiCircleGauge({
    value,
    max = 100,
    label,
    sublabel,
    color,
    size = 120,
    lastCheck,
    icon: Icon,
}: {
    value: number;
    max?: number;
    label: string;
    sublabel?: string;
    color: string;
    size?: number;
    lastCheck?: string;
    icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}) {
    const animatedValue = useAnimatedCounter(value, 1500);
    const percentage = (animatedValue / max) * 100;
    const radius = 40;
    const circumference = Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    const statusText = percentage >= 95 ? "Healthy" : percentage >= 80 ? "Degraded" : "Critical";
    const statusColor = percentage >= 95 ? "text-emerald-400" : percentage >= 80 ? "text-amber-400" : "text-red-400";

    return (
        <div className="flex flex-col items-center gap-2">
            <div className="relative" style={{ width: size, height: size * 0.65 }}>
                <svg viewBox="0 0 100 60" className="h-full w-full overflow-visible">
                    <defs>
                        <linearGradient id={`gauge-grad-${label}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                            <stop offset="100%" stopColor={color} stopOpacity={1} />
                        </linearGradient>
                        <filter id={`gauge-glow-${label}`}>
                            <feGaussianBlur stdDeviation="3" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    {/* Background arc */}
                    <path
                        d="M 10 55 A 40 40 0 0 1 90 55"
                        fill="none"
                        stroke="currentColor"
                        className="text-zinc-200 dark:text-zinc-700"
                        strokeWidth="6"
                        strokeLinecap="round"
                    />
                    {/* Value arc */}
                    <path
                        d="M 10 55 A 40 40 0 0 1 90 55"
                        fill="none"
                        stroke={`url(#gauge-grad-${label})`}
                        strokeWidth="6"
                        strokeLinecap="round"
                        strokeDasharray={`${circumference}`}
                        strokeDashoffset={strokeDashoffset}
                        filter={`url(#gauge-glow-${label})`}
                        className="transition-all duration-1000 ease-out"
                    />
                    {/* Value text */}
                    <text
                        x="50"
                        y="48"
                        textAnchor="middle"
                        className="fill-zinc-800 text-[16px] font-extrabold dark:fill-white"
                    >
                        {animatedValue}%
                    </text>
                </svg>
            </div>
            {Icon && (
                <div
                    className="flex h-8 w-8 items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${color}20` }}
                >
                    <Icon className="h-4 w-4" style={{ color }} />
                </div>
            )}
            <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">{label}</p>
            <p className={`text-[10px] font-semibold ${statusColor}`}>{statusText}</p>
            {sublabel && <p className="text-[10px] text-zinc-400">{sublabel}</p>}
            {lastCheck && (
                <p className="flex items-center gap-1 text-[9px] text-zinc-400">
                    <ClockIcon className="h-3 w-3" /> {lastCheck}
                </p>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Progress Ring (SLA)
   ═══════════════════════════════════════════════════════════════════════════ */
function ProgressRing({
    value,
    label,
    sublabel,
    size = 100,
}: {
    value: number;
    label: string;
    sublabel: string;
    size?: number;
}) {
    const animatedValue = useAnimatedCounter(Math.round(value * 10), 1800) / 10;
    const radius = 38;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (animatedValue / 100) * circumference;
    const color = value >= 95 ? "#22c55e" : value >= 85 ? "#eab308" : "#ef4444";
    const bgColor =
        value >= 95
            ? "from-emerald-500/10 to-emerald-500/5"
            : value >= 85
              ? "from-amber-500/10 to-amber-500/5"
              : "from-red-500/10 to-red-500/5";

    return (
        <div
            className={`flex flex-col items-center gap-3 rounded-2xl border border-zinc-200/30 bg-gradient-to-b ${bgColor} p-5 backdrop-blur-sm transition-all duration-300 hover:scale-[1.03] hover:shadow-lg dark:border-zinc-700/30`}
        >
            <div className="relative" style={{ width: size, height: size }}>
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        className="text-zinc-200 dark:text-zinc-700"
                        strokeWidth="5"
                    />
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth="5"
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        className="transition-all duration-1000 ease-out"
                        style={{ filter: `drop-shadow(0 0 6px ${color}40)` }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-extrabold text-zinc-800 dark:text-white">
                        {animatedValue.toFixed(1)}%
                    </span>
                </div>
            </div>
            <div className="text-center">
                <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">{label}</p>
                <p className="mt-0.5 text-[10px] text-zinc-400">{sublabel}</p>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Mini Sparkline (inline SVG)
   ═══════════════════════════════════════════════════════════════════════════ */
function MiniSparkline({
    data,
    color = "#3b82f6",
    width = 60,
    height = 20,
}: {
    data: number[];
    color?: string;
    width?: number;
    height?: number;
}) {
    if (data.length < 2) return null;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const points = data
        .map((v, i) => {
            const x = (i / (data.length - 1)) * width;
            const y = height - ((v - min) / range) * (height - 4) - 2;
            return `${x},${y}`;
        })
        .join(" ");

    return (
        <svg width={width} height={height} className="inline-block">
            <defs>
                <linearGradient id={`spark-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
            </defs>
            <polyline
                points={points}
                fill="none"
                stroke={color}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <polygon
                points={`0,${height} ${points} ${width},${height}`}
                fill={`url(#spark-${color.replace("#", "")})`}
            />
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Live Indicator
   ═══════════════════════════════════════════════════════════════════════════ */
function LiveIndicator() {
    return (
        <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-500">Live</span>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Geographic Threat Map
   ═══════════════════════════════════════════════════════════════════════════ */
const THREAT_ORIGINS = [
    { country: "Russia", x: 302, y: 53, color: "#ef4444", count: 847 },
    { country: "China", x: 412, y: 78, color: "#f97316", count: 523 },
    { country: "Romania", x: 286, y: 72, color: "#eab308", count: 312 },
    { country: "Nigeria", x: 260, y: 126, color: "#a855f7", count: 198 },
    { country: "Iran", x: 324, y: 90, color: "#ec4899", count: 156 },
];
const MUNICH = { x: 266, y: 65 };

function GeoThreatMap() {
    const [activePulse, setActivePulse] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setActivePulse((prev) => (prev + 1) % THREAT_ORIGINS.length);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative">
            <svg viewBox="0 0 500 280" className="h-full w-full">
                <defs>
                    <filter id="map-glow">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <radialGradient id="pulse-grad" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </radialGradient>
                </defs>

                {/* World map continents */}
                <g transform={`scale(${500 / WORLD_MAP_VIEWBOX.width}, ${280 / WORLD_MAP_VIEWBOX.height})`}>
                    <path d={WORLD_MAP_PATH} fill="currentColor" className="text-zinc-200/60 dark:text-zinc-700/40" />
                </g>

                {/* Connection lines from threat origins to Munich */}
                {THREAT_ORIGINS.map((origin, i) => (
                    <g key={origin.country}>
                        <line
                            x1={origin.x}
                            y1={origin.y}
                            x2={MUNICH.x}
                            y2={MUNICH.y}
                            stroke={origin.color}
                            strokeWidth={i === activePulse ? 2 : 0.8}
                            strokeOpacity={i === activePulse ? 0.7 : 0.2}
                            strokeDasharray={i === activePulse ? "none" : "4 4"}
                            className="transition-all duration-500"
                        />
                        {/* Animated travel dot */}
                        {i === activePulse && (
                            <circle r="3" fill={origin.color} filter="url(#map-glow)">
                                <animateMotion
                                    dur="1.5s"
                                    repeatCount="indefinite"
                                    path={`M ${origin.x} ${origin.y} L ${MUNICH.x} ${MUNICH.y}`}
                                />
                            </circle>
                        )}
                    </g>
                ))}

                {/* Munich target */}
                <circle
                    cx={MUNICH.x}
                    cy={MUNICH.y}
                    r="12"
                    fill="url(#pulse-grad)"
                    className="animate-ping"
                    style={{ animationDuration: "2s" }}
                />
                <circle cx={MUNICH.x} cy={MUNICH.y} r="5" fill="#3b82f6" filter="url(#map-glow)" />
                <text x={MUNICH.x} y={MUNICH.y - 12} textAnchor="middle" className="fill-blue-500 text-[8px] font-bold">
                    MUNICH HQ
                </text>

                {/* Threat origin dots */}
                {THREAT_ORIGINS.map((origin, i) => (
                    <g key={origin.country}>
                        {i === activePulse && (
                            <circle
                                cx={origin.x}
                                cy={origin.y}
                                r="10"
                                fill={origin.color}
                                fillOpacity={0.15}
                                className="animate-ping"
                                style={{ animationDuration: "1.5s" }}
                            />
                        )}
                        <circle
                            cx={origin.x}
                            cy={origin.y}
                            r="4"
                            fill={origin.color}
                            filter="url(#map-glow)"
                            className="transition-all duration-300"
                            style={{ r: i === activePulse ? 6 : 4 } as React.CSSProperties}
                        />
                        <text
                            x={origin.x}
                            y={origin.y - 8}
                            textAnchor="middle"
                            className="fill-zinc-500 text-[7px] font-semibold dark:fill-zinc-400"
                        >
                            {origin.country}
                        </text>
                    </g>
                ))}
            </svg>

            {/* Legend */}
            <div className="mt-4 flex flex-wrap justify-center gap-4">
                {THREAT_ORIGINS.map((origin) => (
                    <div
                        key={origin.country}
                        className="flex items-center gap-2 rounded-lg border border-zinc-200/30 bg-white/50 px-3 py-1.5 text-xs backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/50"
                    >
                        <span
                            className="inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: origin.color, boxShadow: `0 0 6px ${origin.color}40` }}
                        />
                        <span className="font-medium text-zinc-700 dark:text-zinc-300">{origin.country}</span>
                        <span className="font-mono text-zinc-400">{origin.count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Radar Chart — Attack Surface
   ═══════════════════════════════════════════════════════════════════════════ */
const RADAR_AXES = [
    { label: "Network", icon: SignalIcon },
    { label: "Email", icon: EnvelopeIcon },
    { label: "Identity", icon: FingerPrintIcon },
    { label: "Endpoints", icon: DevicePhoneMobileIcon },
    { label: "Applications", icon: CpuChipIcon },
    { label: "Physical", icon: BuildingOfficeIcon },
];
const COVERAGE_VALUES = [92, 88, 76, 85, 71, 68];
const RISK_VALUES = [45, 72, 58, 40, 65, 35];

function RadarChart() {
    const [animated, setAnimated] = useState(false);
    useEffect(() => {
        const timer = setTimeout(() => setAnimated(true), 300);
        return () => clearTimeout(timer);
    }, []);

    const cx = 150,
        cy = 140,
        maxR = 110;
    const numAxes = RADAR_AXES.length;

    const getPoint = (axisIndex: number, value: number) => {
        const angle = (axisIndex * 2 * Math.PI) / numAxes - Math.PI / 2;
        const r = (value / 100) * maxR;
        return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    };

    const makePolygon = (values: number[]) =>
        values
            .map((v, i) => {
                const p = getPoint(i, animated ? v : 0);
                return `${p.x},${p.y}`;
            })
            .join(" ");

    return (
        <svg viewBox="0 0 300 280" className="h-full w-full">
            <defs>
                <linearGradient id="radar-coverage" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#6366f1" stopOpacity={0.15} />
                </linearGradient>
                <linearGradient id="radar-risk" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0.1} />
                </linearGradient>
            </defs>

            {/* Concentric rings */}
            {[20, 40, 60, 80, 100].map((level) => (
                <polygon
                    key={level}
                    points={Array.from({ length: numAxes }, (_, i) => {
                        const p = getPoint(i, level);
                        return `${p.x},${p.y}`;
                    }).join(" ")}
                    fill="none"
                    stroke="currentColor"
                    className="text-zinc-200 dark:text-zinc-700"
                    strokeWidth={level === 100 ? 1.5 : 0.5}
                    strokeOpacity={level === 100 ? 0.5 : 0.3}
                />
            ))}

            {/* Axis lines */}
            {RADAR_AXES.map((_, i) => {
                const p = getPoint(i, 100);
                return (
                    <line
                        key={i}
                        x1={cx}
                        y1={cy}
                        x2={p.x}
                        y2={p.y}
                        stroke="currentColor"
                        className="text-zinc-200 dark:text-zinc-700"
                        strokeWidth="0.5"
                        strokeOpacity="0.4"
                    />
                );
            })}

            {/* Coverage polygon */}
            <polygon
                points={makePolygon(COVERAGE_VALUES)}
                fill="url(#radar-coverage)"
                stroke="#3b82f6"
                strokeWidth="2"
                strokeLinejoin="round"
                className="transition-all duration-[1500ms] ease-out"
                style={{ filter: "drop-shadow(0 0 4px rgba(59,130,246,0.3))" }}
            />

            {/* Risk polygon */}
            <polygon
                points={makePolygon(RISK_VALUES)}
                fill="url(#radar-risk)"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeDasharray="6 3"
                className="transition-all duration-[1500ms] ease-out"
                style={{ filter: "drop-shadow(0 0 4px rgba(239,68,68,0.3))" }}
            />

            {/* Data points */}
            {COVERAGE_VALUES.map((v, i) => {
                const p = getPoint(i, animated ? v : 0);
                return (
                    <circle
                        key={`c-${i}`}
                        cx={p.x}
                        cy={p.y}
                        r="3"
                        fill="#3b82f6"
                        className="transition-all duration-[1500ms] ease-out"
                        style={{ filter: "drop-shadow(0 0 3px rgba(59,130,246,0.5))" }}
                    />
                );
            })}
            {RISK_VALUES.map((v, i) => {
                const p = getPoint(i, animated ? v : 0);
                return (
                    <circle
                        key={`r-${i}`}
                        cx={p.x}
                        cy={p.y}
                        r="3"
                        fill="#ef4444"
                        className="transition-all duration-[1500ms] ease-out"
                        style={{ filter: "drop-shadow(0 0 3px rgba(239,68,68,0.5))" }}
                    />
                );
            })}

            {/* Labels */}
            {RADAR_AXES.map((axis, i) => {
                const p = getPoint(i, 118);
                return (
                    <text
                        key={i}
                        x={p.x}
                        y={p.y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        className="fill-zinc-600 text-[9px] font-bold dark:fill-zinc-400"
                    >
                        {axis.label}
                    </text>
                );
            })}
        </svg>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Real-Time Activity Feed
   ═══════════════════════════════════════════════════════════════════════════ */
const FEED_EVENT_TEMPLATES = [
    { system: "soc" as SystemView, severity: "critical" as const, desc: "Brute force attempt blocked on fw-prod-01" },
    { system: "ndr" as SystemView, severity: "high" as const, desc: "Unusual outbound DNS detected from 10.0.8.55" },
    { system: "mail" as SystemView, severity: "medium" as const, desc: "Phishing email quarantined: fak3-invoice.com" },
    { system: "ztna" as SystemView, severity: "low" as const, desc: "Trust score updated for m.schmidt (87 -> 82)" },
    {
        system: "phishing" as SystemView,
        severity: "medium" as const,
        desc: "Simulation campaign 'Q2 IT Helpdesk' — 3 new clicks",
    },
    {
        system: "pentests" as SystemView,
        severity: "high" as const,
        desc: "CVE-2024-21762 exploit path validated on lb-prod-02",
    },
    { system: "soc" as SystemView, severity: "high" as const, desc: "Malware signature match on edr-ws-207" },
    {
        system: "ndr" as SystemView,
        severity: "critical" as const,
        desc: "C2 beacon traffic blocked: 185.143.223.1:443",
    },
    {
        system: "mail" as SystemView,
        severity: "low" as const,
        desc: "Spam wave from ch34p-meds.ru — 142 emails blocked",
    },
    {
        system: "ztna" as SystemView,
        severity: "high" as const,
        desc: "Session denied: unknown device from 91.215.85.12",
    },
    {
        system: "phishing" as SystemView,
        severity: "low" as const,
        desc: "Training module completed by 12 users (DHL campaign)",
    },
    {
        system: "pentests" as SystemView,
        severity: "medium" as const,
        desc: "SNMP enumeration pattern detected on switch-floor3",
    },
    { system: "soc" as SystemView, severity: "medium" as const, desc: "Suspicious PowerShell execution on ws-fin-04" },
    {
        system: "ndr" as SystemView,
        severity: "high" as const,
        desc: "Lateral movement attempt via SMB from 10.0.15.201",
    },
    {
        system: "mail" as SystemView,
        severity: "critical" as const,
        desc: "Zero-day payload detected in .docx attachment",
    },
];

const SEVERITY_COLORS = {
    critical: "bg-red-500",
    high: "bg-orange-500",
    medium: "bg-amber-400",
    low: "bg-emerald-400",
};

function RealTimeActivityFeed() {
    const [events, setEvents] = useState(() =>
        FEED_EVENT_TEMPLATES.slice(0, 10).map((e, i) => ({
            ...e,
            id: `feed-${i}`,
            timestamp: new Date(Date.now() - (10 - i) * 30000).toLocaleTimeString("de-DE", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            }),
        })),
    );
    const feedRef = useRef<HTMLDivElement>(null);
    const counterRef = useRef(10);

    useEffect(() => {
        const interval = setInterval(() => {
            const template = FEED_EVENT_TEMPLATES[counterRef.current % FEED_EVENT_TEMPLATES.length];
            const newEvent = {
                ...template,
                id: `feed-${counterRef.current}`,
                timestamp: new Date().toLocaleTimeString("de-DE", {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                }),
            };
            counterRef.current += 1;
            setEvents((prev) => [newEvent, ...prev].slice(0, 15));
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div>
            <div className="mb-4 flex items-center justify-between">
                <LiveIndicator />
                <span className="text-[10px] text-zinc-400">{events.length} events</span>
            </div>
            <div ref={feedRef} className="max-h-[400px] space-y-1 overflow-y-auto">
                {events.map((event, i) => (
                    <div
                        key={event.id}
                        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-500 ${i === 0 ? "animate-pulse bg-blue-50/50 dark:bg-blue-950/20" : "hover:bg-zinc-50/80 dark:hover:bg-zinc-800/50"}`}
                        style={{ animationIterationCount: i === 0 ? 3 : 0 }}
                    >
                        <span className="font-mono text-[10px] tabular-nums text-zinc-400">{event.timestamp}</span>
                        <span
                            className={`h-2 w-2 shrink-0 rounded-full ${SEVERITY_COLORS[event.severity]}`}
                            style={{
                                boxShadow: `0 0 4px ${event.severity === "critical" ? "#ef444460" : "transparent"}`,
                            }}
                        />
                        <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                        <span className="flex-1 truncate text-xs text-zinc-700 dark:text-zinc-300">{event.desc}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Risk Score Summary
   ═══════════════════════════════════════════════════════════════════════════ */
const RISK_CATEGORIES = [
    { label: "External Threats", score: 72, color: "#ef4444", sparkData: [45, 52, 61, 58, 67, 72, 70, 75, 72] },
    { label: "Insider Risk", score: 34, color: "#f97316", sparkData: [20, 25, 30, 28, 35, 32, 38, 36, 34] },
    { label: "Compliance", score: 18, color: "#22c55e", sparkData: [30, 28, 25, 22, 20, 18, 19, 17, 18] },
    { label: "Vulnerability", score: 56, color: "#a855f7", sparkData: [40, 45, 50, 48, 55, 52, 58, 54, 56] },
];

function RiskScoreSummary() {
    const overallScore = Math.round(RISK_CATEGORIES.reduce((sum, c) => sum + c.score, 0) / RISK_CATEGORIES.length);
    const animatedScore = useAnimatedCounter(overallScore, 2000);
    const scoreColor = overallScore >= 70 ? "#ef4444" : overallScore >= 40 ? "#f97316" : "#22c55e";
    const scoreLabel = overallScore >= 70 ? "High Risk" : overallScore >= 40 ? "Moderate" : "Low Risk";

    const radius = 55;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (animatedScore / 100) * circumference;

    return (
        <div className="grid gap-6 md:grid-cols-2">
            {/* Overall Score */}
            <div className="flex flex-col items-center justify-center gap-3">
                <div className="relative" style={{ width: 160, height: 160 }}>
                    <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
                        <circle
                            cx="70"
                            cy="70"
                            r={radius}
                            fill="none"
                            stroke="currentColor"
                            className="text-zinc-200 dark:text-zinc-700"
                            strokeWidth="8"
                        />
                        <circle
                            cx="70"
                            cy="70"
                            r={radius}
                            fill="none"
                            stroke={scoreColor}
                            strokeWidth="8"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={strokeDashoffset}
                            className="transition-all duration-[2000ms] ease-out"
                            style={{ filter: `drop-shadow(0 0 8px ${scoreColor}40)` }}
                        />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-4xl font-extrabold text-zinc-800 dark:text-white">{animatedScore}</span>
                        <span className="text-[10px] font-semibold text-zinc-400">/ 100</span>
                    </div>
                </div>
                <span
                    className="rounded-full px-3 py-1 text-xs font-bold"
                    style={{ backgroundColor: `${scoreColor}15`, color: scoreColor }}
                >
                    {scoreLabel}
                </span>
            </div>

            {/* Category Breakdown */}
            <div className="space-y-4">
                {RISK_CATEGORIES.map((cat) => {
                    const catColor = cat.score >= 60 ? "#ef4444" : cat.score >= 35 ? "#f97316" : "#22c55e";
                    return (
                        <div
                            key={cat.label}
                            className="rounded-xl border border-zinc-200/30 bg-white/50 p-3 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/50"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-xs font-bold text-zinc-700 dark:text-zinc-300">{cat.label}</p>
                                    <p className="mt-0.5 text-lg font-extrabold" style={{ color: catColor }}>
                                        {cat.score}
                                    </p>
                                </div>
                                <MiniSparkline data={cat.sparkData} color={cat.color} width={80} height={28} />
                            </div>
                            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-700">
                                <div
                                    className="h-full rounded-full transition-all duration-1000 ease-out"
                                    style={{
                                        width: `${cat.score}%`,
                                        backgroundColor: catColor,
                                        boxShadow: `0 0 6px ${catColor}40`,
                                    }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT: Activity Heatmap (GitHub-style)
   ═══════════════════════════════════════════════════════════════════════════ */
function ActivityHeatmap() {
    const [hoveredCell, setHoveredCell] = useState<{ week: number; day: number } | null>(null);

    const heatmapData = useMemo(() => {
        const data: number[][] = [];
        for (let w = 0; w < 12; w++) {
            const week: number[] = [];
            for (let d = 0; d < 7; d++) {
                // Simulate realistic security event counts
                const base = Math.floor(Math.random() * 30) + 5;
                const spike = Math.random() > 0.85 ? Math.floor(Math.random() * 80) + 40 : 0;
                week.push(base + spike);
            }
            data.push(week);
        }
        return data;
    }, []);

    const maxVal = Math.max(...heatmapData.flat());
    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const getColor = (val: number) => {
        const intensity = val / maxVal;
        if (intensity < 0.15) return "bg-zinc-100 dark:bg-zinc-800";
        if (intensity < 0.35) return "bg-blue-200 dark:bg-blue-900/60";
        if (intensity < 0.55) return "bg-blue-400 dark:bg-blue-700";
        if (intensity < 0.75) return "bg-blue-600 dark:bg-blue-500";
        return "bg-blue-800 dark:bg-blue-400";
    };

    const getDateForCell = (week: number, day: number) => {
        const now = new Date();
        const dayOffset = (11 - week) * 7 + (6 - day);
        const date = new Date(now);
        date.setDate(date.getDate() - dayOffset);
        return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    return (
        <div className="relative">
            <div className="flex gap-1">
                {/* Day labels */}
                <div className="flex flex-col gap-1 pr-2 pt-6">
                    {dayLabels.map((label, i) => (
                        <div key={label} className="flex h-4 items-center">
                            {i % 2 === 0 && <span className="text-[9px] text-zinc-400">{label}</span>}
                        </div>
                    ))}
                </div>

                {/* Grid */}
                <div className="flex flex-1 gap-1">
                    {heatmapData.map((week, wi) => (
                        <div key={wi} className="flex flex-1 flex-col gap-1">
                            {/* Week label */}
                            <div className="flex h-5 items-end justify-center">
                                {wi % 2 === 0 && <span className="text-[8px] text-zinc-400">W{12 - wi}</span>}
                            </div>
                            {week.map((val, di) => (
                                <div
                                    key={di}
                                    className={`relative aspect-square rounded-[3px] ${getColor(val)} cursor-pointer transition-all duration-200 hover:scale-125 hover:ring-2 hover:ring-blue-400/50`}
                                    onMouseEnter={() => setHoveredCell({ week: wi, day: di })}
                                    onMouseLeave={() => setHoveredCell(null)}
                                >
                                    {hoveredCell?.week === wi && hoveredCell?.day === di && (
                                        <div className="absolute -top-10 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-lg border border-zinc-200/50 bg-white/95 px-2.5 py-1.5 shadow-lg backdrop-blur-sm dark:border-zinc-700/50 dark:bg-zinc-900/95">
                                            <p className="text-[10px] font-bold text-zinc-700 dark:text-zinc-300">
                                                {val} events
                                            </p>
                                            <p className="text-[9px] text-zinc-400">{getDateForCell(wi, di)}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center justify-end gap-1.5">
                <span className="text-[9px] text-zinc-400">Less</span>
                {[
                    "bg-zinc-100 dark:bg-zinc-800",
                    "bg-blue-200 dark:bg-blue-900/60",
                    "bg-blue-400 dark:bg-blue-700",
                    "bg-blue-600 dark:bg-blue-500",
                    "bg-blue-800 dark:bg-blue-400",
                ].map((c, i) => (
                    <div key={i} className={`h-3 w-3 rounded-[2px] ${c}`} />
                ))}
                <span className="text-[9px] text-zinc-400">More</span>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   EXISTING COMPONENTS (kept intact)
   ═══════════════════════════════════════════════════════════════════════════ */

function CorrelationGraph({ onNavigate }: { onNavigate: (v: View) => void }) {
    const matrix = useMemo(() => getCorrelationMatrix(), []);
    const cx = 200,
        cy = 180,
        r = 135;
    const nodes = SYSTEM_LIST.map((sys, i) => {
        const angle = (i * Math.PI * 2) / SYSTEM_LIST.length - Math.PI / 2;
        return { sys, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    });

    const edges: { from: number; to: number; weight: number }[] = [];
    for (let i = 0; i < SYSTEM_LIST.length; i++)
        for (let j = i + 1; j < SYSTEM_LIST.length; j++) {
            const w = matrix[i][j];
            if (w > 0) edges.push({ from: i, to: j, weight: w });
        }
    const maxW = Math.max(...edges.map((e) => e.weight), 1);

    return (
        <svg viewBox="0 0 400 360" className="h-full w-full">
            <defs>
                <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                <filter id="glow-strong">
                    <feGaussianBlur stdDeviation="6" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
                {SYSTEM_LIST.map((sys, i) => {
                    const nextSys = SYSTEM_LIST[(i + 1) % SYSTEM_LIST.length];
                    return (
                        <linearGradient key={`grad-${sys}`} id={`edge-grad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={SYS_HEX[sys]} stopOpacity={0.6} />
                            <stop offset="100%" stopColor={SYS_HEX[nextSys]} stopOpacity={0.6} />
                        </linearGradient>
                    );
                })}
            </defs>
            {edges.map((e, i) => (
                <line
                    key={i}
                    x1={nodes[e.from].x}
                    y1={nodes[e.from].y}
                    x2={nodes[e.to].x}
                    y2={nodes[e.to].y}
                    stroke={`url(#edge-grad-${e.from})`}
                    strokeOpacity={0.2 + (e.weight / maxW) * 0.5}
                    strokeWidth={1.5 + (e.weight / maxW) * 5}
                    strokeLinecap="round"
                />
            ))}
            {edges.map((e, i) => {
                const mx = (nodes[e.from].x + nodes[e.to].x) / 2;
                const my = (nodes[e.from].y + nodes[e.to].y) / 2;
                return (
                    <g key={`label-${i}`}>
                        <circle
                            cx={mx}
                            cy={my}
                            r={11}
                            fill="white"
                            fillOpacity={0.85}
                            className="dark:fill-zinc-900"
                            filter="url(#glow)"
                        />
                        <text
                            x={mx}
                            y={my}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className="fill-zinc-500 text-[10px] font-bold"
                        >
                            {e.weight}
                        </text>
                    </g>
                );
            })}
            {nodes.map((n) => (
                <g key={n.sys} onClick={() => onNavigate(n.sys)} className="cursor-pointer">
                    <circle
                        cx={n.x}
                        cy={n.y}
                        r={36}
                        fill={SYS_HEX[n.sys]}
                        fillOpacity={0.08}
                        stroke={SYS_HEX[n.sys]}
                        strokeWidth={1}
                        strokeOpacity={0.2}
                        className="animate-node-pulse"
                    />
                    <circle cx={n.x} cy={n.y} r={26} fill={SYS_HEX[n.sys]} filter="url(#glow-strong)" />
                    <text
                        x={n.x}
                        y={n.y}
                        textAnchor="middle"
                        dominantBaseline="central"
                        className="fill-white text-[9px] font-bold"
                        style={{ pointerEvents: "none" }}
                    >
                        {VIEW_LABELS[n.sys]}
                    </text>
                </g>
            ))}
        </svg>
    );
}

function HeatmapMatrix({ onNavigate }: { onNavigate: (v: View) => void }) {
    const matrix = useMemo(() => getCorrelationMatrix(), []);
    const maxVal = Math.max(...matrix.flat(), 1);

    return (
        <div className="overflow-x-auto">
            <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
                <thead>
                    <tr>
                        <th className="w-16" />
                        {SYSTEM_LIST.map((sys) => (
                            <th key={sys} className="pb-1 text-center">
                                <button
                                    onClick={() => onNavigate(sys)}
                                    className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:text-blue-500"
                                >
                                    {VIEW_LABELS[sys]}
                                </button>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {SYSTEM_LIST.map((rowSys, ri) => (
                        <tr key={rowSys}>
                            <td className="pr-2 text-right">
                                <button
                                    onClick={() => onNavigate(rowSys)}
                                    className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:text-blue-500"
                                >
                                    {VIEW_LABELS[rowSys]}
                                </button>
                            </td>
                            {SYSTEM_LIST.map((colSys, ci) => {
                                const val = matrix[ri][ci];
                                const intensity = val / maxVal;
                                const isDiag = ri === ci;
                                return (
                                    <td key={colSys} className="p-0">
                                        <button
                                            onClick={() => onNavigate(isDiag ? rowSys : colSys)}
                                            className="flex h-11 w-full items-center justify-center rounded-xl text-xs font-bold transition-all duration-200 hover:scale-110 hover:shadow-md hover:shadow-blue-500/20"
                                            style={{
                                                backgroundColor: isDiag
                                                    ? "transparent"
                                                    : `rgba(59, 130, 246, ${intensity * 0.65 + 0.05})`,
                                                color: isDiag ? "#a1a1aa" : intensity > 0.35 ? "white" : "#71717a",
                                                border: isDiag ? "1.5px dashed #3f3f46" : "none",
                                                boxShadow:
                                                    !isDiag && intensity > 0.3
                                                        ? `0 0 12px rgba(59, 130, 246, ${intensity * 0.2})`
                                                        : "none",
                                            }}
                                        >
                                            {isDiag ? "\u2014" : val}
                                        </button>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MitreGrid({ onNavigate }: { onNavigate: (v: View) => void }) {
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {MITRE_TACTICS.map((tactic) => (
                <div
                    key={tactic.id}
                    className="group rounded-xl border border-zinc-200/30 bg-white/40 p-4 backdrop-blur-sm transition-all duration-300 hover:border-blue-400/30 hover:bg-blue-50/30 hover:shadow-md hover:shadow-blue-500/5 dark:border-zinc-700/30 dark:bg-zinc-800/30 dark:hover:border-blue-500/30 dark:hover:bg-blue-950/20"
                >
                    <p className="font-mono text-[10px] text-zinc-400">{tactic.id}</p>
                    <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">{tactic.name}</p>
                    <div className="mt-2.5 flex gap-1.5">
                        {tactic.systems.map((sys) => (
                            <button
                                key={sys}
                                onClick={() => onNavigate(sys)}
                                title={VIEW_LABELS[sys]}
                                className="transition-all duration-200 hover:scale-150"
                            >
                                <span
                                    className="inline-block h-2.5 w-2.5 rounded-full shadow-sm"
                                    style={{ backgroundColor: SYS_HEX[sys], boxShadow: `0 0 6px ${SYS_HEX[sys]}40` }}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ThreatActorCards({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (event: UnifiedEvent) => void;
}) {
    const [expanded, setExpanded] = useState<string | null>(null);

    return (
        <div className="space-y-3">
            {THREAT_ACTORS.map((actor) => (
                <div
                    key={actor.name}
                    className="overflow-hidden rounded-2xl border border-zinc-200/30 shadow-lg shadow-red-500/[0.03] transition-all duration-300 hover:shadow-xl hover:shadow-red-500/[0.06] dark:border-zinc-700/30"
                >
                    <button
                        onClick={() => setExpanded(expanded === actor.name ? null : actor.name)}
                        className="flex w-full items-center justify-between bg-white/60 px-6 py-5 text-left backdrop-blur-xl dark:bg-zinc-800/60"
                    >
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-red-500/15 to-red-600/10 shadow-sm shadow-red-500/10">
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-zinc-900 dark:text-white">
                                        {actor.name}
                                    </span>
                                    <Badge color="red">{actor.type}</Badge>
                                </div>
                                <div className="mt-1 flex items-center gap-3">
                                    <span className="text-xs text-zinc-400">{actor.events.length} events</span>
                                    <span className="text-xs text-zinc-400">{actor.systems.length} systems</span>
                                    <div className="flex gap-1">
                                        {actor.systems.map((sys) => (
                                            <span
                                                key={sys}
                                                className="inline-block h-2 w-2 rounded-full"
                                                style={{ backgroundColor: SYS_HEX[sys] }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ChevronDownIcon
                            className={`h-5 w-5 text-zinc-400 transition-transform ${expanded === actor.name ? "rotate-180" : ""}`}
                        />
                    </button>
                    {expanded === actor.name && (
                        <div className="border-t border-zinc-200/30 bg-zinc-50/40 px-6 py-5 backdrop-blur-sm dark:border-zinc-700/30 dark:bg-zinc-800/30">
                            <div className="grid gap-4 sm:grid-cols-3">
                                <div>
                                    <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                                        Indicators (IPs)
                                    </span>
                                    <div className="mt-1.5 space-y-0.5">
                                        {actor.ips.map((ip) => (
                                            <p key={ip} className="font-mono text-xs text-zinc-700 dark:text-zinc-300">
                                                {ip}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                                        Users
                                    </span>
                                    <div className="mt-1.5 space-y-0.5">
                                        {actor.users.map((u) => (
                                            <p key={u} className="font-mono text-xs text-zinc-700 dark:text-zinc-300">
                                                {u}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">
                                        Assets
                                    </span>
                                    <div className="mt-1.5 space-y-0.5">
                                        {actor.assets.map((a) => (
                                            <p key={a} className="font-mono text-xs text-zinc-700 dark:text-zinc-300">
                                                {a}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 max-h-48 overflow-y-auto">
                                {UNIFIED_EVENTS.filter((e) => actor.events.includes(e.id)).map((event) => (
                                    <div
                                        key={event.id}
                                        className="flex items-center gap-2 border-l-2 border-blue-200 py-1.5 pl-3 dark:border-blue-800"
                                    >
                                        <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                                        <span className="font-mono text-xs text-zinc-400">{event.time}</span>
                                        <span className="flex-1 truncate text-xs text-zinc-700 dark:text-zinc-300">
                                            {event.title}
                                        </span>
                                        <button
                                            onClick={() => onInvestigate(event)}
                                            className="shrink-0 text-zinc-400 hover:text-blue-500"
                                        >
                                            <EyeIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function IncidentBuilder({ onNavigate }: { onNavigate: (v: View) => void }) {
    const [chain, setChain] = useState<UnifiedEvent[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const addable = UNIFIED_EVENTS.filter((e) => !chain.find((c) => c.id === e.id));

    return (
        <div>
            <div className="flex flex-wrap items-center gap-2">
                {chain.length === 0 && (
                    <p className="text-xs italic text-zinc-400">Click + to start building an incident chain</p>
                )}
                {chain.map((event, i) => (
                    <div key={event.id} className="flex items-center gap-1">
                        <div className="flex items-center gap-1.5 rounded-xl border border-zinc-200/40 bg-white/70 px-3 py-1.5 shadow-sm backdrop-blur-sm transition-all duration-200 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/40 dark:bg-zinc-800/70">
                            <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                            <span className="max-w-20 truncate font-mono text-xs text-zinc-600 dark:text-zinc-300">
                                {event.id}
                            </span>
                            <button
                                onClick={() => setChain(chain.filter((c) => c.id !== event.id))}
                                className="text-zinc-300 hover:text-red-500 dark:text-zinc-600"
                            >
                                <XMarkIcon className="h-3 w-3" />
                            </button>
                        </div>
                        {i < chain.length - 1 && <ArrowRightIcon className="h-3.5 w-3.5 text-blue-400/60" />}
                    </div>
                ))}
                <button
                    onClick={() => setShowPicker(!showPicker)}
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-zinc-300/60 text-zinc-400 transition-all duration-300 hover:border-blue-400/60 hover:text-blue-500 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-600/60"
                >
                    <PlusIcon className="h-4 w-4" />
                </button>
            </div>
            {showPicker && (
                <div className="mt-3 max-h-48 overflow-y-auto rounded-2xl border border-zinc-200/40 shadow-lg shadow-blue-500/5 backdrop-blur-xl dark:border-zinc-700/40">
                    {addable.slice(0, 12).map((event) => (
                        <button
                            key={event.id}
                            onClick={() => {
                                setChain([...chain, event]);
                                setShowPicker(false);
                            }}
                            className="flex w-full items-center gap-2 border-b border-zinc-100/60 px-4 py-2.5 text-left transition-all duration-200 last:border-0 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent dark:border-zinc-700/30 dark:hover:from-blue-950/30 dark:hover:to-transparent"
                        >
                            <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                            <span className="font-mono text-xs text-zinc-400">{event.id}</span>
                            <span className="flex-1 truncate text-xs text-zinc-700 dark:text-zinc-300">
                                {event.title}
                            </span>
                        </button>
                    ))}
                </div>
            )}
            {chain.length >= 2 && (
                <div className="mt-3 rounded-2xl border border-blue-200/40 bg-gradient-to-r from-blue-50/70 to-purple-50/30 p-5 shadow-sm shadow-blue-500/5 backdrop-blur-sm dark:border-blue-800/30 dark:from-blue-950/40 dark:to-purple-950/20">
                    <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                        Chain spans {new Set(chain.map((c) => c.system)).size} systems with{" "}
                        {new Set(chain.flatMap((c) => c.correlated)).size} cross-correlations
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                        {[...new Set(chain.map((c) => c.system))].map((sys) => (
                            <button key={sys} onClick={() => onNavigate(sys)}>
                                <Badge color={VIEW_COLORS[sys]}>{VIEW_LABELS[sys]}</Badge>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   SYSTEM ICON MAP
   ═══════════════════════════════════════════════════════════════════════════ */
const SYSTEM_ICONS: Record<SystemView, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
    soc: ShieldCheckIcon,
    ndr: SignalIcon,
    mail: EnvelopeIcon,
    ztna: LockClosedIcon,
    phishing: BugAntIcon,
    pentests: WrenchScrewdriverIcon,
};

const SYSTEM_HEALTH: Record<SystemView, { health: number; lastCheck: string }> = {
    soc: { health: 99, lastCheck: "12s ago" },
    ndr: { health: 97, lastCheck: "8s ago" },
    mail: { health: 94, lastCheck: "15s ago" },
    ztna: { health: 98, lastCheck: "5s ago" },
    phishing: { health: 87, lastCheck: "1m ago" },
    pentests: { health: 82, lastCheck: "3m ago" },
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT: OverviewDashboard (massively overengineered)
   ═══════════════════════════════════════════════════════════════════════════ */
export function OverviewDashboard({
    onNavigate,
    onInvestigate,
}: {
    onNavigate: (v: View) => void;
    onInvestigate: (event: UnifiedEvent) => void;
}) {
    const { t } = useTranslation();
    const sortedEvents = useMemo(() => [...UNIFIED_EVENTS].sort((a, b) => b.time.localeCompare(a.time)), []);
    const totalCorrelations = UNIFIED_EVENTS.reduce((sum, e) => sum + e.correlated.length, 0);
    const [showAllEvents, setShowAllEvents] = useState(false);
    const visibleEvents = showAllEvents ? sortedEvents : sortedEvents.slice(0, 8);

    // Generate fake sparkline data for each event in the timeline
    const eventSparklines = useMemo(() => {
        const map = new Map<string, number[]>();
        for (const event of UNIFIED_EVENTS) {
            const data: number[] = [];
            for (let i = 0; i < 12; i++) {
                data.push(Math.floor(Math.random() * 20) + 2);
            }
            map.set(event.id, data);
        }
        return map;
    }, []);

    // Severity border colors for enhanced timeline
    const severityBorderColors: Record<string, string> = {
        critical: "border-l-red-500",
        high: "border-l-orange-500",
        medium: "border-l-amber-400",
        low: "border-l-emerald-400",
    };

    return (
        <>
            {/* ══════════════════════════════════════════════════════════════
                HERO STATS (existing)
               ══════════════════════════════════════════════════════════════ */}
            <div
                className="animate-gradient-shift mb-10 rounded-2xl px-8 pb-10 pt-10"
                style={{
                    background:
                        "linear-gradient(135deg, #1e40af 0%, #1d4ed8 25%, #2563eb 50%, #4f46e5 75%, #6366f1 100%)",
                    backgroundSize: "200% 200%",
                }}
            >
                <div className="relative">
                    <div className="absolute -right-20 -top-20 h-60 w-60 rounded-full bg-white/5 blur-3xl" />
                    <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-purple-500/10 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 shadow-lg shadow-blue-900/30 backdrop-blur-sm">
                            <ChartBarIcon className="h-7 w-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-extrabold tracking-tight text-white">{t("overview.title")}</h1>
                            <p className="text-sm font-medium text-blue-200/80">Real-time cross-system intelligence</p>
                        </div>
                    </div>
                    <div className="relative mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
                        {[
                            {
                                label: t("overview.crossCorrelations"),
                                value: String(totalCorrelations),
                                sub: "+23% vs. last week",
                            },
                            { label: t("overview.mailToBlock"), value: "< 8s", sub: "-2.1s improvement" },
                            { label: t("overview.pentestExploited"), value: "2 / 24", sub: "PT-001, PT-004 active" },
                            { label: t("overview.systemsCovered"), value: "4.2", sub: "avg. systems per incident" },
                        ].map((kpi) => (
                            <div
                                key={kpi.label}
                                className="group rounded-2xl border border-white/10 bg-white/10 p-5 shadow-lg shadow-blue-900/20 backdrop-blur-md transition-all duration-300 hover:scale-[1.03] hover:bg-white/15 hover:shadow-xl"
                            >
                                <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-200/70">
                                    {kpi.label}
                                </p>
                                <p
                                    className="mt-2 text-3xl font-extrabold tracking-tight text-white"
                                    style={{ textShadow: "0 0 20px rgba(255,255,255,0.15)" }}
                                >
                                    {kpi.value}
                                </p>
                                <p className="mt-1.5 text-xs font-medium text-blue-200/50">{kpi.sub}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                NEW: System Health Dashboard
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="System Health Dashboard"
                description="Real-time health monitoring across all six platform systems with auto-refresh diagnostics."
            />
            <Card>
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-6">
                    {SYSTEM_LIST.map((sys) => (
                        <button
                            key={sys}
                            onClick={() => onNavigate(sys)}
                            className="group transition-all duration-300 hover:scale-105"
                        >
                            <SemiCircleGauge
                                value={SYSTEM_HEALTH[sys].health}
                                label={VIEW_LABELS[sys]}
                                color={SYS_HEX[sys]}
                                icon={SYSTEM_ICONS[sys]}
                                lastCheck={SYSTEM_HEALTH[sys].lastCheck}
                                size={110}
                            />
                        </button>
                    ))}
                </div>
            </Card>

            {/* ══════════════════════════════════════════════════════════════
                NEW: Geographic Threat Map
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="Geographic Threat Intelligence"
                description="Live visualization of attack origin countries with animated threat corridors converging on Munich HQ."
            />
            <Card>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-red-500/15 to-orange-500/10">
                            <GlobeAltIcon className="h-5 w-5 text-red-500" />
                        </div>
                        <div>
                            <Subheading>Active Threat Origins</Subheading>
                            <p className="text-xs text-zinc-400">
                                Last 24 hours — {THREAT_ORIGINS.reduce((s, o) => s + o.count, 0).toLocaleString()} total
                                events
                            </p>
                        </div>
                    </div>
                    <LiveIndicator />
                </div>
                <div className="mt-4">
                    <GeoThreatMap />
                </div>
            </Card>

            {/* ══════════════════════════════════════════════════════════════
                NEW: Radar Chart + Risk Score (side by side)
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="Attack Surface Analysis & Risk Score"
                description="Comprehensive radar view of coverage vs. risk across six security domains, with overall risk scoring."
            />
            <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                    <div className="mb-4 flex items-center justify-between">
                        <Subheading>Attack Surface Radar</Subheading>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                                <span className="inline-block h-2.5 w-6 rounded-full bg-blue-500" /> Coverage
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                                <span
                                    className="inline-block h-2.5 w-6 rounded-full bg-red-500"
                                    style={{
                                        backgroundImage:
                                            "repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.5) 3px, rgba(255,255,255,0.5) 6px)",
                                    }}
                                />{" "}
                                Risk
                            </div>
                        </div>
                    </div>
                    <div style={{ height: 280 }}>
                        <RadarChart />
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        {RADAR_AXES.map((axis, i) => (
                            <div
                                key={axis.label}
                                className="flex items-center gap-2 rounded-lg border border-zinc-200/20 px-2.5 py-1.5 dark:border-zinc-700/20"
                            >
                                <axis.icon className="h-3.5 w-3.5 text-zinc-400" />
                                <div className="flex-1">
                                    <p className="text-[10px] font-bold text-zinc-600 dark:text-zinc-400">
                                        {axis.label}
                                    </p>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-blue-500">{COVERAGE_VALUES[i]}%</span>
                                        <span className="text-[10px] text-zinc-300 dark:text-zinc-600">/</span>
                                        <span className="text-[10px] text-red-500">{RISK_VALUES[i]}%</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>

                <Card>
                    <Subheading>Composite Risk Score</Subheading>
                    <p className="mb-4 mt-1 text-xs text-zinc-400">
                        Aggregated risk assessment across threat vectors with trend analysis
                    </p>
                    <RiskScoreSummary />
                </Card>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                NEW: Real-Time Activity Feed
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="Real-Time Activity Feed"
                description="Auto-updating stream of security events across all systems. New events appear every 5 seconds."
            />
            <Card>
                <RealTimeActivityFeed />
            </Card>

            {/* ══════════════════════════════════════════════════════════════
                NEW: SLA Compliance Grid
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="SLA Compliance Metrics"
                description="Service Level Agreement performance tracking with color-coded thresholds (green > 95%, yellow > 85%, red < 85%)."
            />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <ProgressRing value={98.2} label="Detection" sublabel="Target: < 5 min" />
                <ProgressRing value={94.7} label="Response" sublabel="Target: < 15 min" />
                <ProgressRing value={91.3} label="Containment" sublabel="Target: < 1 hour" />
                <ProgressRing value={87.8} label="Resolution" sublabel="Target: < 4 hours" />
            </div>

            {/* ══════════════════════════════════════════════════════════════
                NEW: Activity Heatmap
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title="Activity Heatmap"
                description="GitHub-style visualization of security event volume over the past 12 weeks. Darker cells indicate higher activity."
            />
            <Card>
                <div className="flex items-center justify-between">
                    <Subheading>Event Volume — Last 12 Weeks</Subheading>
                    <div className="flex items-center gap-2">
                        <FireIcon className="h-4 w-4 text-orange-500" />
                        <span className="text-xs font-medium text-zinc-500">Hover for details</span>
                    </div>
                </div>
                <div className="mt-4">
                    <ActivityHeatmap />
                </div>
            </Card>

            {/* ══════════════════════════════════════════════════════════════
                EXISTING: System Interconnections
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title={t("overview.correlationGraph")}
                description="Visualize how all platform systems are interconnected through shared incidents and correlations."
            />
            <div className="grid gap-6 lg:grid-cols-5">
                <Card className="lg:col-span-3">
                    <div style={{ height: 360 }}>
                        <CorrelationGraph onNavigate={onNavigate} />
                    </div>
                </Card>
                <Card className="lg:col-span-2">
                    <Subheading>{t("overview.heatmap")}</Subheading>
                    <p className="mb-4 mt-1 text-xs text-zinc-400">Pair-wise correlation intensity between systems</p>
                    <HeatmapMatrix onNavigate={onNavigate} />
                </Card>
            </div>

            {/* MITRE ATT&CK */}
            <SectionTitle
                title={t("overview.mitreMap")}
                description="Kill chain coverage across all integrated systems — every tactic has multi-system detection."
            />
            <Card>
                <MitreGrid onNavigate={onNavigate} />
                <div className="mt-4 flex flex-wrap gap-4 border-t border-zinc-200/60 pt-4 dark:border-zinc-700/60">
                    {SYSTEM_LIST.map((sys) => (
                        <button
                            key={sys}
                            onClick={() => onNavigate(sys)}
                            className="flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-zinc-700 dark:hover:text-zinc-300"
                        >
                            <span
                                className="inline-block h-2.5 w-2.5 rounded-full shadow-sm"
                                style={{ backgroundColor: SYS_HEX[sys] }}
                            />
                            {VIEW_LABELS[sys]}
                        </button>
                    ))}
                </div>
            </Card>

            {/* Threat Intelligence */}
            <SectionTitle
                title={t("overview.threatActors")}
                description="Tracked threat actors across the platform with full indicator and event mapping."
            />
            <ThreatActorCards onNavigate={onNavigate} onInvestigate={onInvestigate} />

            {/* Incident Builder */}
            <SectionTitle
                title={t("overview.incidentBuilder")}
                description="Manually correlate events from any system into custom incident chains."
            />
            <Card>
                <IncidentBuilder onNavigate={onNavigate} />
            </Card>

            {/* ══════════════════════════════════════════════════════════════
                ENHANCED: Unified Timeline (with severity borders, threat
                actor pills, mini sparklines)
               ══════════════════════════════════════════════════════════════ */}
            <SectionTitle
                title={t("overview.unifiedTimeline")}
                description={`${UNIFIED_EVENTS.length} events from ${SYSTEM_LIST.length} systems — sorted by time, showing cross-system correlations.`}
            />
            <Card>
                <div className="space-y-0">
                    {visibleEvents.map((event) => (
                        <div
                            key={event.id}
                            className={`relative border-l-[3px] ${severityBorderColors[event.severity]} py-3.5 pl-6 transition-all duration-200 hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-transparent dark:hover:from-blue-950/10 dark:hover:to-transparent`}
                        >
                            <div
                                className={`absolute -left-[7px] top-5 h-3.5 w-3.5 rounded-full border-2 shadow-sm ${event.severity === "critical" ? "border-red-500 bg-red-50 shadow-red-400/30 dark:bg-red-950" : event.severity === "high" ? "border-orange-500 bg-orange-50 shadow-orange-400/30 dark:bg-orange-950" : event.severity === "medium" ? "border-amber-400 bg-amber-50 shadow-amber-300/30 dark:bg-amber-950" : "border-emerald-400 bg-emerald-50 shadow-emerald-300/30 dark:bg-emerald-950"}`}
                            />
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        <Badge color={VIEW_COLORS[event.system]}>{VIEW_LABELS[event.system]}</Badge>
                                        <span className="font-mono text-[11px] text-zinc-400">{event.id}</span>
                                        <span className="font-mono text-[11px] text-zinc-400">{event.time}</span>
                                        <Badge
                                            color={
                                                event.severity === "critical"
                                                    ? "red"
                                                    : event.severity === "high"
                                                      ? "orange"
                                                      : event.severity === "medium"
                                                        ? "yellow"
                                                        : "zinc"
                                            }
                                        >
                                            {event.severity}
                                        </Badge>
                                        {/* Threat actor pill */}
                                        {event.actor && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-red-500/10 to-red-600/5 px-2.5 py-0.5 text-[10px] font-bold text-red-600 ring-1 ring-red-500/20 dark:from-red-500/20 dark:to-red-600/10 dark:text-red-400 dark:ring-red-500/30">
                                                <ExclamationTriangleIcon className="h-2.5 w-2.5" />
                                                {event.actor}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-3">
                                        <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                                            {event.title}
                                        </p>
                                        {/* Mini sparkline for event frequency */}
                                        <MiniSparkline
                                            data={eventSparklines.get(event.id) ?? [1, 1, 1]}
                                            color={SYS_HEX[event.system]}
                                            width={50}
                                            height={16}
                                        />
                                    </div>
                                    <p className="mt-0.5 text-xs text-zinc-500">{event.details}</p>
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {event.correlated.map((sys) => (
                                            <button key={sys} onClick={() => onNavigate(sys)}>
                                                <Badge color={VIEW_COLORS[sys]}>
                                                    <LinkIcon className="mr-0.5 inline h-2.5 w-2.5" />
                                                    {VIEW_LABELS[sys]}
                                                </Badge>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={() => onInvestigate(event)}
                                    className="shrink-0 rounded-xl p-2 text-zinc-300 transition-all duration-200 hover:bg-blue-50/80 hover:text-blue-600 hover:shadow-sm hover:shadow-blue-500/10 dark:text-zinc-600 dark:hover:bg-blue-950/50 dark:hover:text-blue-400"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {sortedEvents.length > 8 && (
                    <button
                        onClick={() => setShowAllEvents(!showAllEvents)}
                        className="mt-5 flex w-full items-center justify-center gap-1.5 rounded-xl border border-zinc-200/40 py-3 text-xs font-semibold text-zinc-500 transition-all duration-300 hover:border-blue-400/40 hover:text-blue-600 hover:shadow-md hover:shadow-blue-500/10 dark:border-zinc-700/40 dark:hover:border-blue-500/40 dark:hover:text-blue-400"
                    >
                        <ChevronDownIcon
                            className={`h-4 w-4 transition-transform ${showAllEvents ? "rotate-180" : ""}`}
                        />
                        {showAllEvents ? "Show less" : `Show all ${sortedEvents.length} events`}
                    </button>
                )}
            </Card>
        </>
    );
}
